import * as React from "react";
import * as moment from "moment";
import { ICenterToCenterDistance, IContact, ILogisticsProvider, ITransportType } from "@nkr/models/index";
import { ActiveLogisticsPlan } from "@nkr/enums/index";
import { TableWrapper, TableHeadWrapper, LogisticsTableBodyWrapper, TimezoneValueWrapper, TableWrapperLogistics } from "../../../styles";
import { FormControlWrapper } from "../../styles";
import { ILogisticsState } from "./logistics-state";
import { Glyphicon, Button, ButtonGroup, FormControl } from "react-bootstrap";
import SelectTimezone from "./select-timezone";
import MaskedImport from "react-maskedinput";
import styled from "styled-components";

const TableBodyWrapper = styled.tbody`
    & > tr > td {
        padding: 2px !important;
    }

    & > tr > td > div > div > span {
    }
`;

interface IProps {
    data: ILogisticsState,
    swapNumber: string,
    donorCenter: string,
    donorAlias: string,
    rdnCenterId: number,
    rdnCenterAlias: string
    recipientCenter: string,
    recipientAlias: string,
    donorCenterId: number,
    recipientCenterId: number,
    earliestPickup: string,
    latestDropoff: string,
    logisticsProviders: ILogisticsProvider[],
    transportTypes: ITransportType[],
    donorSurgeons: IContact[],
    recipientSurgeons: IContact[],
    valid: boolean,
    forCourier: boolean,
    distanceBetweenCentersList: ICenterToCenterDistance[],
    onDateTimeFieldChange: (field: string, value: moment.Moment | string) => void,
    onFieldChange: (field: string, value: string) => void,
    onDropdownChange: (id: string, value: string) => void,
    onActiveLogisticsPlanChange: (value: ActiveLogisticsPlan) => void,
    onCopyPrimary: () => void,
    onCopyBackup: () => void
}

const LogisticsEditContent = (props: IProps) => {
    const handleJobNumberChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        props.onFieldChange('jobNumber', e.currentTarget.value);
    };

    const handleSelectChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        let name = e.currentTarget.name;
        props.onFieldChange(name, e.currentTarget.value);
    };

    const handleDateTimeFieldChange = (field: string, value: string) => {
        props.onDateTimeFieldChange(field, value);
    };

    const handleInputChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let field = e.currentTarget.id;
        props.onFieldChange(field, e.currentTarget.value);
    };

    const handleDropdownChange = (id: string, value: string) => {
        props.onDropdownChange(id, value);
    };

    const handleActiveLogisticsPlanChange = (activeLogisticsPlan: ActiveLogisticsPlan) => {
        props.onActiveLogisticsPlanChange(activeLogisticsPlan);
    };

    const handleCopyPrimary = () => {
        props.onCopyPrimary();
    };

    const handleCopyBackup = () => {
        props.onCopyBackup();
    };

    const { swapNumber, donorCenter, donorAlias, rdnCenterId, rdnCenterAlias, recipientCenter, recipientAlias, earliestPickup, latestDropoff, valid } = props;

    const transportMethod = props.transportTypes.find(tt => tt.id == (props.data.transportMethod != "" ? parseInt(props.data.transportMethod) : 0));
    const transportMethodBackup = props.transportTypes.find(tt => tt.id == (props.data.transportMethodBackup != "" ? parseInt(props.data.transportMethodBackup) : 0));
    const transportMethodActual = props.transportTypes.find(tt => tt.id == (props.data.transportMethodActual != "" ? parseInt(props.data.transportMethodActual) : 0));

    const singleLegs: string[] = ["Commercial", "Charter", "Ground", "Internal", "Comm OBC - Single Leg"];

    const disableLeg2 = transportMethod != null ? singleLegs.includes(transportMethod.description) : false;
    const disableLeg2Backup = transportMethodBackup != null ? singleLegs.includes(transportMethodBackup.description) : false;
    const disableLeg2Actual = transportMethodActual != null ? singleLegs.includes(transportMethodActual.description) : false;

    let centerToCenter = props.distanceBetweenCentersList.find(x => x.donorCenterId == props.donorCenterId && x.recipientCenterId == props.recipientCenterId);
    let distance = centerToCenter != null ? centerToCenter.distanceBetweenCenters : 0;

    const fontSize = props.forCourier ? 12 : 12;

    const logisticsProvider = props.logisticsProviders.find(x => x.id.toString() == props.data.logisticsProvider);
    const logisticsProviderName = logisticsProvider != null ? logisticsProvider.description : '';

    return (
        <>
            {!valid ?
                <div className="alert alert-danger">The fields outlined in red have an invalid value</div>
                : null}

            <div style={{ border: "1px solid #bce8f1", backgroundColor: "#d9edf7", color: "#31708f", marginTop: 5, marginBottom: 5, padding: 7, display: "flex", justifyContent: "space-around", alignItems: "center" }}>
                <div className="text-center">
                    <span style={{ fontWeight: 700 }}>Swap #</span><br />
                    <span>{swapNumber}</span>
                </div>

                <div style={{ display: 'flex', alignItems: "center" }}>
                    <div className="text-center">
                        <span style={{ fontWeight: 700 }}>{donorCenter}</span>
                        {rdnCenterId > 0 ?
                            <span style={{ paddingLeft: 5 }}>(<span style={{ color: "red" }}>{rdnCenterAlias}</span>)</span>
                            : null}
                        <br />
                        <span>{donorAlias}</span>
                    </div>

                    <div className="text-center" style={{ width: 50 }}>
                        <Glyphicon glyph="forward" />
                    </div>

                    <div className="text-center">
                        <span style={{ fontWeight: 700 }}>{recipientCenter}</span><br />
                        <span>{recipientAlias}</span>
                    </div>
                </div>

                <div className="text-center">
                    <span style={{ fontWeight: 700 }}>Earliest Pickup</span><br />
                    <span>{earliestPickup}</span>
                </div>
                <div className="text-center">
                    <span style={{ fontWeight: 700 }}>Latest Dropoff</span><br />
                    <span>{latestDropoff}</span>
                </div>
                <div className="text-center">
                    <span style={{ fontWeight: 700 }}>Dist</span><br />
                    <span>{distance} Miles</span>
                </div>
            </div>

            <div style={{ color: "#8a6d3b", backgroundColor: "#fcf8e3", border: "1px solid #faebcc", padding: 7, marginBottom: 10 }}>
                <TableWrapper className="condensed" style={{ marginBottom: 0 }}>
                    <LogisticsTableBodyWrapper fontSize={props.forCourier ? 14 : 12}>
                        {!props.forCourier ?
                            <>
                                <tr>
                                    <td style={{ textAlign: "right" }}>Logistics Provider:</td>
                                    <td>
                                        <FormControlWrapper
                                            componentClass="select"
                                            name='logisticsProvider'
                                            value={props.data.logisticsProvider}
                                            onChange={handleSelectChange}
                                        >
                                            <option value="">-- select --</option>
                                            {props.logisticsProviders.filter(item => item.isActive).map((item, index) =>
                                                <option key={index} value={item.id}>{item.description}</option>
                                            )}
                                        </FormControlWrapper>
                                    </td>
                                    <td style={{ textAlign: "right" }}>Job Number:</td>
                                    <td>
                                        <FormControlWrapper
                                            type="text"
                                            value={props.data.jobNumber}
                                            onChange={handleJobNumberChange}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ textAlign: "right" }}>Donor Surgeon:</td>
                                    <td>
                                        <FormControlWrapper
                                            componentClass="select"
                                            name='donorSurgeonContactId'
                                            value={props.data.donorSurgeonContactId}
                                            style={{ width: "initial" }}
                                            onChange={handleSelectChange}
                                        >
                                            <option value="">-- select --</option>
                                            {props.donorSurgeons.map((item, index) =>
                                                <option key={index} value={item.id}>{item.fullName}</option>
                                            )}
                                        </FormControlWrapper>
                                    </td>
                                    <td style={{ textAlign: "right" }}>Recipient Surgeon:</td>
                                    <td>
                                        <FormControlWrapper
                                            componentClass="select"
                                            name='recipientSurgeonContactId'
                                            value={props.data.recipientSurgeonContactId}
                                            style={{ width: "initial" }}
                                            onChange={handleSelectChange}
                                        >
                                            <option value="">-- select --</option>
                                            {props.recipientSurgeons.map((item, index) =>
                                                <option key={index} value={item.id}>{item.fullName}</option>
                                            )}
                                        </FormControlWrapper>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ textAlign: "right" }}>Donor Coordinator:</td>
                                    <td>
                                        {props.data.donorCoordinator}
                                    </td>
                                    <td style={{ textAlign: "right" }}>Recipient Coordinator:</td>
                                    <td>
                                        {props.data.recipientCoordinator}
                                    </td>
                                </tr>
                            </>
                            :
                            <>
                                <tr>
                                    <td style={{ textAlign: "right" }}>Logistics Provider:</td>
                                    <td>
                                        {logisticsProviderName}
                                    </td>
                                    <td style={{ textAlign: "right" }}>Job Number:</td>
                                    <td>
                                        <FormControlWrapper
                                            type="text"
                                            value={props.data.jobNumber}
                                            onChange={handleJobNumberChange}
                                        />
                                    </td>
                                </tr>

                                <tr>
                                    <td style={{ textAlign: "right" }}>Donor Coordinator:</td>
                                    <td>
                                        {props.data.donorCoordinator}
                                    </td>
                                    <td style={{ textAlign: "right" }}>Recipient Coordinator:</td>
                                    <td>
                                        {props.data.recipientCoordinator}
                                    </td>
                                </tr>
                            </>
                        }
                    </LogisticsTableBodyWrapper>
                </TableWrapper>
            </div>

            <div>
                <TableWrapperLogistics className="table-condensed" fontSize={14}>
                    <TableHeadWrapper>
                        <tr>
                            <th></th>
                            <th className="text-center" style={{ width: "24.5%" }}>
                                {props.data.activeLogisticsPlan == ActiveLogisticsPlan.Primary.toString() ?
                                    <Button bsStyle="primary" bsSize="xs" onClick={() => handleActiveLogisticsPlanChange(ActiveLogisticsPlan.Primary)}>
                                        Primary <Glyphicon glyph="ok" />
                                    </Button>
                                    :
                                    <Button bsSize="xs" onClick={() => handleActiveLogisticsPlanChange(ActiveLogisticsPlan.Primary)}>Primary</Button>
                                }
                            </th>
                            <th className="text-center" style={{ width: "24.5%" }}>
                                {props.data.activeLogisticsPlan == ActiveLogisticsPlan.Backup.toString() ?
                                    <Button bsStyle="primary" bsSize="xs" onClick={() => handleActiveLogisticsPlanChange(ActiveLogisticsPlan.Backup)}>
                                        Backup <Glyphicon glyph="ok" />
                                    </Button>
                                    :
                                    <Button bsSize="xs" onClick={() => handleActiveLogisticsPlanChange(ActiveLogisticsPlan.Backup)}>Backup</Button>
                                }
                            </th>
                            <th className="text-center" style={{ width: "24.5%" }}>
                                <span style={{ marginRight: 10 }}>Actual</span>
                                <ButtonGroup>
                                    <Button bsSize="xs" onClick={handleCopyPrimary}><div style={{ lineHeight: "15px" }}>Copy</div><div style={{ lineHeight: "15px" }}>Primary</div></Button>
                                    <Button bsSize="xs" onClick={handleCopyBackup}><div style={{ lineHeight: "15px" }}>Copy</div><div style={{ lineHeight: "15px" }}>Backup</div></Button>
                                </ButtonGroup>
                            </th>
                        </tr>
                    </TableHeadWrapper>
                    <TableBodyWrapper>
                        <tr>
                            <td style={{ fontWeight: 700 }}>Donor Surgery/Pickup Date/Time</td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.kidneyPickupDateTimeValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        value={props.data.kidneyPickupDateTime}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("kidneyPickupDateTime", e.currentTarget.value)}
                                    />
                                    <TimezoneValueWrapper className="form-control">{props.data.kidneyPickupDateTimeTz}</TimezoneValueWrapper>
                                </div>
                            </td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.kidneyPickupDateTimeBackupValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        value={props.data.kidneyPickupDateTimeBackup}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("kidneyPickupDateTimeBackup", e.currentTarget.value)}
                                    />
                                    <TimezoneValueWrapper className="form-control">{props.data.kidneyPickupDateTimeTz}</TimezoneValueWrapper>
                                </div>
                            </td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.kidneyPickupDateTimeActualValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        value={props.data.kidneyPickupDateTimeActual}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("kidneyPickupDateTimeActual", e.currentTarget.value)}
                                    />
                                    <TimezoneValueWrapper className="form-control">{props.data.kidneyPickupDateTimeTz}</TimezoneValueWrapper>
                                </div>
                            </td>
                        </tr>
                        {!props.forCourier && (
                            <tr>
                                <td style={{ fontWeight: 700 }}>PU Ground Miles</td>
                                <td>
                                    <FormControlWrapper
                                        type="text"
                                        value={props.data.pickupGroundMiles}
                                        id="pickupGroundMiles"
                                        onChange={handleInputChange}
                                    />
                                </td>
                                <td>
                                    <FormControlWrapper
                                        type="text"
                                        value={props.data.pickupGroundMilesBackup}
                                        id="pickupGroundMilesBackup"
                                        onChange={handleInputChange}
                                    />
                                </td>
                                <td>
                                    <FormControlWrapper
                                        type="text"
                                        value={props.data.pickupGroundMilesActual}
                                        id="pickupGroundMilesActual"
                                        onChange={handleInputChange}
                                    />
                                </td>
                            </tr>
                        )}
                        <tr>
                            <td style={{ fontWeight: 700 }}>Flight Route or Ground</td>
                            <td>
                                <FormControlWrapper
                                    type="text"
                                    value={props.data.flightRouteOrGround}
                                    id="flightRouteOrGround"
                                    onChange={handleInputChange}
                                />
                            </td>
                            <td>
                                <FormControlWrapper
                                    type="text"
                                    value={props.data.flightRouteOrGroundBackup}
                                    id="flightRouteOrGroundBackup"
                                    onChange={handleInputChange}
                                />
                            </td>
                            <td>
                                <FormControlWrapper
                                    type="text"
                                    value={props.data.flightRouteOrGroundActual}
                                    id="flightRouteOrGroundActual"
                                    onChange={handleInputChange}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 700 }}>Transport Method</td>
                            <td>
                                <FormControlWrapper
                                    componentClass="select"
                                    value={props.data.transportMethod}
                                    id="transportMethod"
                                    onChange={handleInputChange}
                                >
                                    <option value=''>- select -</option>
                                    {props.transportTypes.map((tt, index) =>
                                        <option key={index} value={tt.id}>{tt.description}</option>
                                    )}
                                </FormControlWrapper>
                            </td>
                            <td>
                                <FormControlWrapper
                                    componentClass="select"
                                    value={props.data.transportMethodBackup}
                                    id="transportMethodBackup"
                                    onChange={handleInputChange}
                                >
                                    <option value=''>- select -</option>
                                    {props.transportTypes.map((tt, index) =>
                                        <option key={index} value={tt.id}>{tt.description}</option>
                                    )}
                                </FormControlWrapper>
                            </td>
                            <td>
                                <FormControlWrapper
                                    componentClass="select"
                                    value={props.data.transportMethodActual}
                                    id="transportMethodActual"
                                    onChange={handleInputChange}
                                >
                                    <option value=''>- select -</option>
                                    {props.transportTypes.map((tt, index) =>
                                        <option key={index} value={tt.id}>{tt.description}</option>
                                    )}
                                </FormControlWrapper>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 700 }}>Transportation Cost</td>
                            <td>
                                <FormControlWrapper
                                    type="text"
                                    value={props.forCourier ? props.data.courierTransportCost : props.data.transportCost}
                                    id="transportCost"
                                    disabled={props.forCourier}
                                    onChange={handleInputChange}
                                />
                            </td>
                            <td>
                                <FormControlWrapper
                                    type="text"
                                    value={props.forCourier ? props.data.courierTransportCostBackup : props.data.transportCostBackup}
                                    id="transportCostBackup"
                                    disabled={props.forCourier}
                                    onChange={handleInputChange}
                                />
                            </td>
                            <td>
                                {props.forCourier == false && (
                                    <FormControlWrapper
                                        type="text"
                                        value={props.data.transportCostActual}
                                        id="transportCostActual"
                                        onChange={handleInputChange}
                                    />
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 700 }}>Flight Carrier &amp; Number</td>
                            <td>
                                <FormControlWrapper
                                    type="text"
                                    value={props.data.flightCarrierAndNumber}
                                    id="flightCarrierAndNumber"
                                    onChange={handleInputChange}
                                />
                            </td>
                            <td>
                                <FormControlWrapper
                                    type="text"
                                    value={props.data.flightCarrierAndNumberBackup}
                                    id="flightCarrierAndNumberBackup"
                                    onChange={handleInputChange}
                                />
                            </td>
                            <td>
                                <FormControlWrapper
                                    type="text"
                                    value={props.data.flightCarrierAndNumberActual}
                                    id="flightCarrierAndNumberActual"
                                    onChange={handleInputChange}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 700 }}>Tendered to Airline Date/Time</td>
                            <td></td>
                            <td></td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.tenderedToAirlineActualValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        value={props.data.tenderedToAirlineActual}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("tenderedToAirlineActual", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="tenderedToAirlineActualTz"
                                        value={props.data.tenderedToAirlineActualTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 700 }}>Leg 1 Departure Date/Time</td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.flightDepartureTimeValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        value={props.data.flightDepartureTime}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("flightDepartureTime", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="flightDepartureTimeTz"
                                        value={props.data.flightDepartureTimeTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.flightDepartureTimeBackupValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        value={props.data.flightDepartureTimeBackup}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("flightDepartureTimeBackup", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="flightDepartureTimeBackupTz"
                                        value={props.data.flightDepartureTimeBackupTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.flightDepartureTimeActualValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        value={props.data.flightDepartureTimeActual}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("flightDepartureTimeActual", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="flightDepartureTimeActualTz"
                                        value={props.data.flightDepartureTimeActualTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 700 }}>Leg 1 Arrival Date/Time</td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.flightArrivalTimeValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        value={props.data.flightArrivalTime}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("flightArrivalTime", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="flightArrivalTimeTz"
                                        value={props.data.flightArrivalTimeTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.flightArrivalTimeBackupValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        value={props.data.flightArrivalTimeBackup}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("flightArrivalTimeBackup", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="flightArrivalTimeBackupTz"
                                        value={props.data.flightArrivalTimeBackupTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.flightArrivalTimeActualValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        value={props.data.flightArrivalTimeActual}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("flightArrivalTimeActual", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="flightArrivalTimeActualTz"
                                        value={props.data.flightArrivalTimeActualTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 700 }}>Leg 2 Departure Date/Time</td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.leg2DepartureTimeValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        disabled={disableLeg2}
                                        value={props.data.leg2DepartureTime}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("leg2DepartureTime", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="leg2DepartureTimeTz"
                                        value={props.data.leg2DepartureTimeTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.leg2DepartureTimeBackupValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        disabled={disableLeg2Backup}
                                        value={props.data.leg2DepartureTimeBackup}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("leg2DepartureTimeBackup", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="leg2DepartureTimeBackupTz"
                                        value={props.data.leg2DepartureTimeBackupTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.leg2DepartureTimeActualValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        disabled={disableLeg2Actual}
                                        value={props.data.leg2DepartureTimeActual}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("leg2DepartureTimeActual", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="leg2DepartureTimeActualTz"
                                        value={props.data.leg2DepartureTimeActualTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 700 }}>Leg 2 Arrival Date/Time</td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.leg2ArrivalTimeValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        disabled={disableLeg2}
                                        value={props.data.leg2ArrivalTime}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("leg2ArrivalTime", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="leg2ArrivalTimeTz"
                                        value={props.data.leg2ArrivalTimeTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.leg2ArrivalTimeBackupValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        disabled={disableLeg2Backup}
                                        value={props.data.leg2ArrivalTimeBackup}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("leg2ArrivalTimeBackup", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="leg2ArrivalTimeBackupTz"
                                        value={props.data.leg2ArrivalTimeBackupTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.leg2ArrivalTimeActualValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        disabled={disableLeg2Actual}
                                        value={props.data.leg2ArrivalTimeActual}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("leg2ArrivalTimeActual", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="leg2ArrivalTimeActualTz"
                                        value={props.data.leg2ArrivalTimeActualTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 700 }}>Recovered from Airline Date/Time</td>
                            <td></td>
                            <td></td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.recoveredFromAirlineActualValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        value={props.data.recoveredFromAirlineActual}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("recoveredFromAirlineActual", e.currentTarget.value)}
                                    />
                                    <SelectTimezone
                                        id="recoveredFromAirlineActualTz"
                                        value={props.data.recoveredFromAirlineActualTz}
                                        fontSize={fontSize}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 700 }}>Kidney Arrival Date/Time</td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.kidneyArrivalDateTimeValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        value={props.data.kidneyArrivalDateTime}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("kidneyArrivalDateTime", e.currentTarget.value)}
                                    />
                                    <TimezoneValueWrapper className="form-control">{props.data.kidneyArrivalDateTimeTz}</TimezoneValueWrapper>
                                </div>
                            </td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.kidneyArrivalDateTimeBackupValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        value={props.data.kidneyArrivalDateTimeBackup}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("kidneyArrivalDateTimeBackup", e.currentTarget.value)}
                                    />
                                    <TimezoneValueWrapper className="form-control">{props.data.kidneyArrivalDateTimeTz}</TimezoneValueWrapper>
                                </div>
                            </td>
                            <td>
                                <div style={{ display: "flex" }}>
                                    <MaskedImport
                                        className='form-control'
                                        style={{ padding: "3px 6px", height: 26, borderColor: props.data.kidneyArrivalDateTimeActualValid ? "#ccc" : "#a94442" }}
                                        mask='11/11/11 11:11'
                                        placeholder={'mm/dd/yy hh:mm'}
                                        value={props.data.kidneyArrivalDateTimeActual}
                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => handleDateTimeFieldChange("kidneyArrivalDateTimeActual", e.currentTarget.value)}
                                    />
                                    <TimezoneValueWrapper className="form-control">{props.data.kidneyArrivalDateTimeTz}</TimezoneValueWrapper>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 700 }}>Delivery Location</td>
                            <td></td>
                            <td></td>
                            <td>
                                <FormControlWrapper
                                    type="text"
                                    value={props.data.deliveryLocation}
                                    id="deliveryLocation"
                                    onChange={handleInputChange}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 700 }}>Signed For</td>
                            <td></td>
                            <td></td>
                            <td>
                                <FormControlWrapper
                                    type="text"
                                    value={props.data.deliveredTo}
                                    id="deliveredTo"
                                    onChange={handleInputChange}
                                />
                            </td>
                        </tr>
                    </TableBodyWrapper>
                </TableWrapperLogistics>
            </div>
        </>
    );
}

export default LogisticsEditContent;